import { useState } from "react";
import { FileSignature, Send, CheckCircle, Link2, Pencil, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useLoad, Loading, EmptyState, Panel, DataTable, StatusBadge, fmtDate, gbp, copyLink, origin } from "./shared";
import { ClientSelect } from "./ClientSelect";

export const ProposalDialog = ({ call, proposal, clientId, onClose, onSaved }) => {
  const [form, setForm] = useState({
    client_id: proposal?.client_id || clientId || "", title: proposal?.title || "", scope: proposal?.scope || "",
    price_amount: proposal?.price_amount ?? "", billing_type: proposal?.billing_type || "one_off", valid_until: proposal?.valid_until || "",
  });
  const set = (k) => (e) => setForm(p => ({ ...p, [k]: e?.target ? e.target.value : e }));
  const save = async () => {
    if (!form.client_id || !form.title || !form.scope || !form.price_amount) return toast.error("Client, title, scope and price are required");
    const body = { ...form, price_amount: Number(form.price_amount), valid_until: form.valid_until || null };
    if (proposal) await call(`/proposals/${proposal.proposal_id}`, { method: "PATCH", body });
    else await call("/proposals", { method: "POST", body });
    toast.success(proposal ? "Proposal updated" : "Proposal created"); onSaved();
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-xl" data-testid="proposal-dialog">
        <DialogHeader><DialogTitle>{proposal ? "Edit proposal" : "New proposal"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5"><Label>Client *</Label><ClientSelect call={call} value={form.client_id} onChange={set("client_id")} disabled={!!proposal || !!clientId} testid="proposal-client-select" /></div>
          <div className="space-y-1.5"><Label>Title *</Label><Input value={form.title} onChange={set("title")} data-testid="proposal-title" /></div>
          <div className="space-y-1.5"><Label>Scope of work *</Label><Textarea rows={6} value={form.scope} onChange={set("scope")} data-testid="proposal-scope" /></div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5"><Label>Price (£, ex VAT) *</Label><Input type="number" min="0" value={form.price_amount} onChange={set("price_amount")} data-testid="proposal-price" /></div>
            <div className="space-y-1.5"><Label>Billing</Label>
              <Select value={form.billing_type} onValueChange={set("billing_type")}>
                <SelectTrigger data-testid="proposal-billing"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="one_off">One-off</SelectItem><SelectItem value="monthly">Monthly</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Valid until</Label><Input type="date" value={form.valid_until} onChange={set("valid_until")} data-testid="proposal-valid-until" /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={save} data-testid="proposal-save-btn">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ProposalActions = ({ p, call, reload, onEdit }) => {
  const send = async () => { const r = await call(`/proposals/${p.proposal_id}/send`, { method: "POST", body: { origin_url: origin() } }); toast.success("Proposal emailed to client"); copyLink(r.public_url); reload(); };
  const accept = async () => { await call(`/proposals/${p.proposal_id}/accept`, { method: "POST", body: { origin_url: origin() } }); toast.success("Accepted — agreement sent for signature"); reload(); };
  return (
    <div className="flex flex-wrap gap-1.5">
      {p.status !== "accepted" && p.status !== "declined" && <>
        <Button size="sm" variant="outline" onClick={() => onEdit(p)} data-testid={`proposal-edit-${p.proposal_id}`}><Pencil className="h-3.5 w-3.5" /></Button>
        <Button size="sm" variant="outline" onClick={send} data-testid={`proposal-send-${p.proposal_id}`}><Send className="h-3.5 w-3.5 mr-1" />{p.status === "sent" ? "Resend" : "Send"}</Button>
        <Button size="sm" variant="outline" onClick={accept} data-testid={`proposal-accept-${p.proposal_id}`}><CheckCircle className="h-3.5 w-3.5 mr-1" />Mark accepted</Button>
      </>}
      {p.public_url && <Button size="sm" variant="ghost" onClick={() => copyLink(p.public_url)} data-testid={`proposal-copy-${p.proposal_id}`}><Link2 className="h-3.5 w-3.5" /></Button>}
    </div>
  );
};

const Proposals = ({ call }) => {
  const { data, loading, reload } = useLoad(call, "/proposals");
  const [editing, setEditing] = useState(null);
  if (loading) return <Loading />;
  const rows = data?.proposals || [];
  return (
    <>
      <div className="flex justify-end mb-3"><Button className="bg-indigo-600 hover:bg-indigo-700" onClick={() => setEditing({})} data-testid="new-proposal-btn"><Plus className="h-4 w-4 mr-1" />New proposal</Button></div>
      {rows.length ? (
        <Panel>
          <DataTable testid="proposals-table" rowKey="proposal_id" rows={rows} columns={[
            { key: "title", label: "Proposal", render: r => <div><p className="font-medium">{r.title}</p><p className="text-xs text-slate-500">{r.client_name}</p></div> },
            { key: "price", label: "Value", render: r => `${gbp(r.price_amount)}${r.billing_type === "monthly" ? "/mo" : ""}` },
            { key: "status", label: "Status", render: r => <StatusBadge status={r.status} testid={`proposal-status-${r.proposal_id}`} /> },
            { key: "created_at", label: "Created", render: r => fmtDate(r.created_at) },
            { key: "act", label: "", render: r => <ProposalActions p={r} call={call} reload={reload} onEdit={setEditing} /> },
          ]} />
        </Panel>
      ) : <EmptyState testid="proposals-empty" icon={FileSignature} title="No proposals yet" text="Create a proposal for a client; once accepted, an agreement is generated for e-signature." />}
      {editing && <ProposalDialog call={call} proposal={editing.proposal_id ? editing : null} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />}
    </>
  );
};

const Agreements = ({ call }) => {
  const { data, loading } = useLoad(call, "/agreements");
  if (loading) return <Loading />;
  const rows = data?.agreements || [];
  if (!rows.length) return <EmptyState testid="agreements-empty" icon={FileSignature} title="No agreements yet" text="Agreements are created automatically when a proposal is accepted." />;
  return (
    <Panel>
      <DataTable testid="agreements-table" rowKey="agreement_id" rows={rows} columns={[
        { key: "title", label: "Agreement", render: r => <div><p className="font-medium">{r.title}</p><p className="text-xs text-slate-500">{r.client_name}</p></div> },
        { key: "price", label: "Value", render: r => `${gbp(r.price_amount)}${r.billing_type === "monthly" ? "/mo" : ""}` },
        { key: "status", label: "Status", render: r => <StatusBadge status={r.status} testid={`agreement-status-${r.agreement_id}`} /> },
        { key: "signed", label: "Signed", render: r => r.signed_at ? `${r.signer_name} · ${fmtDate(r.signed_at)}` : "—" },
        { key: "invoice_id", label: "Invoice", render: r => <span className="font-mono text-xs">{r.invoice_id || "—"}</span> },
        { key: "act", label: "", render: r => <Button size="sm" variant="ghost" onClick={() => copyLink(r.public_url)} data-testid={`agreement-copy-${r.agreement_id}`}><Link2 className="h-3.5 w-3.5" /></Button> },
      ]} />
    </Panel>
  );
};

export const CommerceProposals = ({ call }) => (
  <Tabs defaultValue="proposals" data-testid="commerce-proposals">
    <TabsList>
      <TabsTrigger value="proposals" data-testid="proposals-tab">Proposals</TabsTrigger>
      <TabsTrigger value="agreements" data-testid="agreements-tab">Agreements</TabsTrigger>
    </TabsList>
    <TabsContent value="proposals" className="mt-4"><Proposals call={call} /></TabsContent>
    <TabsContent value="agreements" className="mt-4"><Agreements call={call} /></TabsContent>
  </Tabs>
);
